import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, Grid, IconButton, InputAdornment, InputLabel, Select,
    Snackbar, TextField, Tooltip, Typography
} from "@mui/material";
import {
    Search as IconSearch,
    Delete as IconDelete, Close as IconClose
} from "@mui/icons-material";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import * as periodoService from "../../services/periodoService";
import * as filialService from "../../services/filialService";
import * as catalogoService from "../../services/catalogoService";
import comprobanteService from "../../services/comprobanteService";
import terapiaPeriodoPagoService from "../../services/terapiaPeriodoPagoService";
import DataGridBase from "../../components/common/DataGridBase";
import DialogoBusqueda from "../../components/common/DialogoBusqueda";
import DialogYesNo from "../../components/DialogYesNo";
import ComprobanteDialogo from "../../components/ComprobanteDialogo";
import ComprobanteMantenimientoDialogo from "../../components/ComprobanteMantenimientoDialogo";
import ComprobantePagoDialogo from "../../components/ComprobantePagoDialogo";
import { PaperComponentDraggable } from "../../components/common/PaperComponentDraggable";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Navegabilidad de los pagos de los periodos de terapia: se ven todos los pagos
// (un periodo puede tener mas de uno), con el estado del comprobante de cada uno,
// las opciones Registrar, Asignar y Pagar segun el rol del usuario, y la opcion
// de eliminar el pago.

// Catalogo 143 = Estados del pago del comprobante.
const ESTADO_PAGO_PENDIENTE = 144;
const ESTADO_PAGO_CANCELADO = 145;
const ESTADO_PAGO_CONSOLIDADO = 227;

// Catalogo 1 = Estados basicos (2 Activo, 4 Anulado).
const ESTADO_ACTIVO = 2;
const ESTADO_ANULADO = 4;

const Index = () => {
    const { isReadOnly, idFilial, perfilCargado } = useMantenimiento("terapiaPeriodoPago");
    const accesos = useSelector((state) => state.seguridad?.accesos) || [];
    const seguridadActiva = useSelector((state) => state.seguridad?.seguridadActiva) || "N";

    // Las opciones Registrar, Pagar y Asignar son para usuarios con rol Secretaria, Administrador y Director.
    const puedeRegistrarPagar = !isReadOnly;
    const puedeAsignar = seguridadActiva === "S"
        ? (accesos.includes("terapiaPeriodoPago.comprobante") || accesos.includes("terapiaPeriodoPago.editar"))
        : true;

    const [filters, setFilters] = useState({
        idPeriodo: 0,
        idFilial: 0,
        idTipoTerapia: 0,
        idEstadoPago: 0,
        idEstado: 2,
    });

    const [listPeriodos, setListPeriodos] = useState([]);
    const [listFilial, setListFilial] = useState([]);
    const [listTiposTerapias, setListTiposTerapias] = useState([]);

    const [cargando, setCargando] = useState(true);
    const [results, setResults] = useState([]);
    const navigate = useNavigate();

    const [openDialogEliminar, setOpenDialogEliminar] = useState(false);
    const [pagoSeleccionado, setPagoSeleccionado] = useState(null);
    // Comprobante que se esta viendo en la ventana de consulta.
    const [comprobanteViendo, setComprobanteViendo] = useState(0);

    // Ventana dialog para registrar el comprobante cuando la fila no tiene uno.
    const [openDialogComprobante, setOpenDialogComprobante] = useState(false);
    const [pagoComprobante, setPagoComprobante] = useState(null);
    const [prefillComprobante, setPrefillComprobante] = useState(null);
    const [detalleComprobante, setDetalleComprobante] = useState([]);

    // Ventana dialog para registrar el pago del comprobante cuando Saldo > 0.
    const [openDialogPago, setOpenDialogPago] = useState(false);
    const [idComprobantePago, setIdComprobantePago] = useState(0);

    // Ventana dialog para Asignar un comprobante con Saldo Disponible > 0.
    const [openDialogAsignar, setOpenDialogAsignar] = useState(false);
    const [openDialogDesvincular, setOpenDialogDesvincular] = useState(false);
    const [pagoDesvincular, setPagoDesvincular] = useState(null);
    const [desvinculando, setDesvinculando] = useState(false);
    const [pagoAsignar, setPagoAsignar] = useState(null);
    const [comprobanteElegidoAsignar, setComprobanteElegidoAsignar] = useState(null);
    const [asignando, setAsignando] = useState(false);

    // Ventana dialog de busqueda de comprobantes con Saldo Disponible > 0.
    const [buscandoComprobanteSaldo, setBuscandoComprobanteSaldo] = useState(false);
    const [buscandoComprobanteSaldoCargando, setBuscandoComprobanteSaldoCargando] = useState(false);
    const [filtrosComprobanteSaldo, setFiltrosComprobanteSaldo] = useState({
        serie: "",
        numero: "",
        pagador: "",
    });
    const [comprobantesConSaldo, setComprobantesConSaldo] = useState([]);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const mensaje = (severity, texto) => {
        setPropsMessage({ severity, message: texto });
        setOpenMessage(true);
    };

    const calcularMontosFila = (row) => {
        if (!row) return { montoTerapia: 0, montoDescuento: 0, total: 0 };
        const montoTerapia = parseFloat(row.montoTarifa) || parseFloat(row.monto) || 0;
        const descRaw = parseFloat(row.montoDescuento) || 0;
        const tieneDescuento = (row.idDescuento > 0 && descRaw > 0 && (!row.idEstadoDescuento || row.idEstadoDescuento === ESTADO_ACTIVO))
            || (descRaw > 0 && (!row.idEstadoDescuento || row.idEstadoDescuento === ESTADO_ACTIVO));
        const montoDescuento = tieneDescuento ? descRaw : 0;
        const total = Math.max(montoTerapia - montoDescuento, 0);
        return { montoTerapia, montoDescuento, total };
    };

    const fetchResults = useCallback(async () => {
        setCargando(true);

        const data = await terapiaPeriodoPagoService.getsTerapiaPeriodoPagoView({
            idPeriodo: filters.idPeriodo == null ? 0 : filters.idPeriodo,
            idFilial: filters.idFilial == null ? 0 : filters.idFilial,
            idTipoTerapia: filters.idTipoTerapia == null ? 0 : filters.idTipoTerapia,
            idEstadoPago: filters.idEstadoPago == null ? 0 : filters.idEstadoPago,
            idEstado: filters.idEstado == null ? 0 : filters.idEstado,
        })
            .catch((err) => { console.log("Err: ", err) });

        // El grid pide un id unico por fila: el pago se identifica por el periodo
        // de terapia y el numero de pago.
        setResults((data || []).map((row) => ({
            ...row,
            idTerapiaPeriodo: row.id,
            id: row.id + "-" + row.numero,
        })));
        setCargando(false);
    }, [filters]);

    const fetchListPeriodos = useCallback(async () => {
        const data = await periodoService.getsPeriodoView({
            idFilial: 0,
            idTipoTerapia: 0,
            idEstadoApertura: 0,
            mesesHaciaAtras: 0,
            idEstado: 0,
        })
            .catch((err) => { console.log("Err: ", err) });

        setListPeriodos(data || []);
    }, []);

    const fetchListFilial = useCallback(async () => {
        const data = await filialService.getFilialesList()
            .catch((err) => { console.log("Err: ", err) });

        setListFilial(data || []);
    }, []);

    const fetchListTiposTerapias = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(49)
            .catch((err) => { console.log("Err: ", err) });

        setListTiposTerapias(data || []);
    }, []);

    useEffect(() => {
        fetchResults();
        fetchListPeriodos();
        fetchListTiposTerapias();
    }, []);

    useEffect(() => {
        if (!perfilCargado) return;
        filialService.getFilialesList()
            .then((data) => {
                setListFilial(filialService.soloFilialDelUsuario(data, idFilial));
            })
            .catch(() => {});
        if (idFilial > 0) {
            setFilters((f) => ({ ...f, idFilial }));
        }
    }, [perfilCargado, idFilial]);

    // Abre el dialogo para registrar el comprobante respetando la regla de descuento:
    // 1 comprobantedetalle cuando no tiene descuento y 2 cuando hay descuento activo.
    const abrirRegistroComprobante = (row) => {
        const hoy = new Date().toISOString().substring(0, 10);
        const { montoTerapia, montoDescuento } = calcularMontosFila(row);

        const detalle = [{
            id: 0,
            numero: 1,
            cantidad: 1,
            cantidadStock: 0,
            idConceptoCobro: row.idTarifa,
            conceptoCobro: row.concepto || "",
            precioUnitario: montoTerapia,
            subTotal: montoTerapia,
        }];

        if (montoDescuento > 0) {
            detalle.push({
                id: 0,
                numero: 2,
                cantidad: 1,
                cantidadStock: 0,
                idConceptoCobro: 999,
                conceptoCobro: "Descuento en Servicios de Terapias",
                precioUnitario: -montoDescuento,
                subTotal: -montoDescuento,
            });
        }

        setPagoComprobante(row);
        setDetalleComprobante(detalle);
        setPrefillComprobante({
            fecha: hoy,
            idTipo: 141,
            idMoneda: 137,
            tipoCambio: 1,
            idCobrador: row.idTerapeuta > 0 ? row.idTerapeuta : 0,
            cobrador: row.idTerapeuta > 0 ? (row.terapeuta || "") : "",
            idPagador: row.idPersona > 0 ? row.idPersona : 0,
            pagador: row.idPersona > 0 ? (row.participante || "") : "",
            observaciones: "Periodo de terapia " + (row.codigoPeriodo || "")
                + " - " + (row.participante || ""),
        });
        setOpenDialogComprobante(true);
    };

    const comprobanteGuardado = async (idComprobante) => {
        setOpenDialogComprobante(false);

        const row = pagoComprobante;
        setPagoComprobante(null);

        if (!row || !idComprobante) {
            fetchResults();
            return;
        }

        try {
            await terapiaPeriodoPagoService.addTerapiaPeriodoPago({
                idTerapiaPeriodo: row.idTerapiaPeriodo,
                numero: row.numero,
                idComprobante: idComprobante,
                idItem: null,
                observaciones: row.observaciones || "",
            });

            fetchResults();
            mensaje("success", "Se registro el comprobante y se asocio al pago del periodo.");
        } catch (err) {
            console.log("Err: ", err);
            fetchResults();
            const detalleErr = err?.response?.data?.detail;
            mensaje("error", detalleErr || "Se registro el comprobante, pero no se pudo asociar al pago del periodo.");
        }
    };

    const abrirRegistroPago = (row) => {
        setIdComprobantePago(row.idComprobante);
        setOpenDialogPago(true);
    };

    const pagoRegistrado = () => {
        setOpenDialogPago(false);
        fetchResults();
        mensaje("success", "Se registro el pago del comprobante.");
    };

    // ---------- Asignar Comprobante (solo Administrador y Director) ----------

    const abrirDialogAsignar = (row) => {
        if (!puedeAsignar) return;
        setPagoAsignar(row);
        setComprobanteElegidoAsignar(null);
        setOpenDialogAsignar(true);
    };

    const cerrarDialogAsignar = () => {
        setOpenDialogAsignar(false);
        setPagoAsignar(null);
        setComprobanteElegidoAsignar(null);
    };

    const buscarComprobantesConSaldo = async () => {
        setBuscandoComprobanteSaldoCargando(true);

        const response = await comprobanteService.getsComprobanteResumenViewFilter({
            fechaInicio: null,
            fechaFin: null,
            idTipo: 0,
            serie: filtrosComprobanteSaldo.serie == null ? "" : filtrosComprobanteSaldo.serie,
            numero: filtrosComprobanteSaldo.numero == null || filtrosComprobanteSaldo.numero === ""
                ? 0
                : parseInt(filtrosComprobanteSaldo.numero) || 0,
            idCobrador: 0,
            cobrador: "",
            idPagador: 0,
            pagador: filtrosComprobanteSaldo.pagador == null ? "" : filtrosComprobanteSaldo.pagador,
            idEstadoPago: ESTADO_PAGO_CANCELADO,
            idEstado: ESTADO_ACTIVO,
            soloConSaldoDisponible: true,
        }).catch((err) => {
            console.log("Err: ", err);
            return null;
        });

        const lista = (response?.data?.data || []).filter(
            (row) => row.idEstadoPago == ESTADO_PAGO_CANCELADO && (parseFloat(row.saldoDisponible) || 0) > 0
        );

        setComprobantesConSaldo(lista);
        setBuscandoComprobanteSaldoCargando(false);
    };

    const abrirBusquedaComprobanteSaldo = () => {
        setFiltrosComprobanteSaldo({ serie: "", numero: "", pagador: "" });
        setBuscandoComprobanteSaldo(true);
        buscarComprobantesConSaldo();
    };

    const elegirComprobanteParaAsignar = async (compFila) => {
        if (!compFila || !pagoAsignar) return;

        // Consultar el comprobante actualizado para validar que aun tenga saldo disponible > 0
        // y que cubra el monto neto de la terapia.
        const resp = await comprobanteService.getComprobanteView(compFila.id)
            .catch(() => null);
        const compActual = resp?.data?.data || compFila;

        const saldoDisponible = parseFloat(compActual.saldoDisponible) || 0;
        const { total } = calcularMontosFila(pagoAsignar);

        if (saldoDisponible <= 0) {
            mensaje("warning", "El comprobante seleccionado ya no cuenta con saldo disponible.");
            buscarComprobantesConSaldo();
            return;
        }

        if (saldoDisponible < total) {
            mensaje(
                "warning",
                `El comprobante seleccionado no tiene suficiente saldo disponible (${saldoDisponible.toFixed(2)}) para cubrir el monto de la terapia (${total.toFixed(2)}).`
            );
            return;
        }

        const serieNumero = compFila.serieNumero
            || (compActual.serie + "-" + String(compActual.numero).padStart(6, "0"));

        setComprobanteElegidoAsignar({
            id: compActual.id || compFila.id,
            serieNumero: serieNumero,
            saldoDisponible: saldoDisponible,
        });
        setBuscandoComprobanteSaldo(false);
    };

    const abrirDialogDesvincular = (row) => {
        if (!puedeAsignar) return;
        setPagoDesvincular(row);
        setOpenDialogDesvincular(true);
    };

    const confirmarDesvincularComprobante = async () => {
        if (!pagoDesvincular) return;
        setDesvinculando(true);
        try {
            await terapiaPeriodoPagoService.desvincularComprobante(
                pagoDesvincular.idTerapiaPeriodo,
                pagoDesvincular.numero
            );
            setOpenDialogDesvincular(false);
            setPagoDesvincular(null);
            fetchResults();
            mensaje("success", "Se desvinculó el comprobante del periodo de terapia con éxito.");
        } catch (err) {
            console.log("Err: ", err);
            const detalleErr = err?.response?.data?.detail || err?.response?.data?.Detail;
            mensaje("error", detalleErr || "No se pudo desvincular el comprobante.");
        } finally {
            setDesvinculando(false);
        }
    };

    const confirmarAsignacionComprobante = async () => {
        if (!pagoAsignar) return;

        if (!comprobanteElegidoAsignar || !comprobanteElegidoAsignar.id) {
            mensaje("warning", "Seleccione un comprobante con saldo disponible.");
            return;
        }

        setAsignando(true);
        try {
            // Al confirmar la asignacion se valida nuevamente que el saldo disponible del
            // comprobante cubra el total del pago.
            const resp = await comprobanteService.getComprobanteView(comprobanteElegidoAsignar.id)
                .catch(() => null);
            const compActual = resp?.data?.data;
            const saldoActual = compActual ? (parseFloat(compActual.saldoDisponible) || 0) : 0;
            const { total } = calcularMontosFila(pagoAsignar);

            if (!compActual || compActual.idEstadoPago != ESTADO_PAGO_CANCELADO || saldoActual <= 0) {
                mensaje("warning", "El comprobante seleccionado ya no cuenta con saldo disponible.");
                setAsignando(false);
                return;
            }

            if (saldoActual < total) {
                mensaje(
                    "warning",
                    `El saldo disponible del comprobante (${saldoActual.toFixed(2)}) no cubre el total del pago (${total.toFixed(2)}).`
                );
                setComprobanteElegidoAsignar((prev) => prev ? { ...prev, saldoDisponible: saldoActual } : null);
                setAsignando(false);
                return;
            }

            await terapiaPeriodoPagoService.asignarComprobanteTerapiaPeriodoPago({
                idTerapiaPeriodo: pagoAsignar.idTerapiaPeriodo,
                numero: pagoAsignar.numero,
                idComprobante: comprobanteElegidoAsignar.id,
                idItem: null,
                observaciones: pagoAsignar.observaciones || "",
            });

            cerrarDialogAsignar();
            fetchResults();
            mensaje("success", "Se asigno el comprobante al pago del periodo.");
        } catch (err) {
            console.log("Err: ", err);
            const detalleErr = err?.response?.data?.detail;
            mensaje(detalleErr ? "warning" : "error", detalleErr || "No se pudo asignar el comprobante al pago del periodo.");
        } finally {
            setAsignando(false);
        }
    };

    // Elimina el enlace del pago para poder volver a generarlo. El comprobante no
    // se borra: su pago se anula desde la pantalla de comprobantes.
    const eliminarPago = async () => {
        if (!pagoSeleccionado) return;

        let detalleErr = "";
        const eliminado = await terapiaPeriodoPagoService
            .deleteTerapiaPeriodoPago(pagoSeleccionado.idTerapiaPeriodo, pagoSeleccionado.numero)
            .catch((err) => {
                console.log("Err: ", err);
                detalleErr = err?.response?.data?.detail || err?.response?.data?.Detail || "";
                return false;
            });

        setPagoSeleccionado(null);

        if (!eliminado) {
            setOpenDialogEliminar(false);
            mensaje("warning", detalleErr || "No se puede anular un pago de periodo de terapia que tiene asignado un comprobante vigente.");
            return;
        }

        mensaje("success", "Se anulo el pago del periodo.");
        fetchResults();
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 60, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'codigoFilial', headerName: 'Filial', width: 70, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'participante', headerName: 'Participante', width: 175, sortable: false, headerAlign: 'left' },
        { field: 'terapeuta', headerName: 'Terapeuta', width: 175, sortable: false, headerAlign: 'left' },
        { field: 'codigoPeriodo', headerName: 'Periodo', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'monto', headerName: 'Monto', width: 95, sortable: false, headerAlign: 'right', align: 'right'
            , renderCell: (cellValues) => {
                const row = cellValues.row;
                const valor = parseFloat(row.montoTarifa) || parseFloat(row.monto) || 0;
                return valor.toFixed(2);
            }
        },
        { field: 'montoDescuento', headerName: 'Descuento', width: 95, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => value == null ? "" : (parseFloat(value) || 0).toFixed(2) },
        { field: 'serieNumero', headerName: 'Comprobante', width: 125, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                const row = cellValues.row;

                if (!row.idComprobante || row.idComprobante == 0 || !row.serieNumero) {
                    if (!puedeRegistrarPagar || row.idEstado == ESTADO_ANULADO) return "";
                    return (
                        <Box
                            sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                            onClick={() => abrirRegistroComprobante(row)}
                        >
                            {"Registrar"}
                        </Box>
                    );
                }

                // El comprobante se muestra en una ventana para consultarlo sin
                // salir del listado.
                return <Box sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                    onClick={() => setComprobanteViendo(row.idComprobante)}>
                    {row.serieNumero}
                </Box>;
            }
        },
        { field: 'asignar', headerName: 'Asignar / Desvincular', width: 145, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                const row = cellValues.row;
                if (row.idEstado == ESTADO_ANULADO) return "";
                if (row.idComprobante && row.idComprobante > 0 && row.serieNumero) {
                    if (!puedeAsignar) return "";
                    return (
                        <Box
                            sx={{ cursor: 'pointer', color: 'error.main', textDecoration: 'underline', fontWeight: 500 }}
                            onClick={() => abrirDialogDesvincular(row)}
                        >
                            {"Desvincular"}
                        </Box>
                    );
                }
                if (!puedeAsignar) return "";
                return (
                    <Box
                        sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                        onClick={() => abrirDialogAsignar(row)}
                    >
                        {"Asignar"}
                    </Box>
                );
            }
        },
        { field: 'montoSaldo', headerName: 'Saldo', width: 95, sortable: false, headerAlign: 'right', align: 'right'
            , renderCell: (cellValues) => {
                const row = cellValues.row;
                if (!row.idComprobante || row.idComprobante == 0) return "";
                return (parseFloat(row.montoSaldo) || 0).toFixed(2);
            }
        },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 115, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                const row = cellValues.row;
                if (!row.idComprobante || row.idComprobante == 0) return "";
                if (parseFloat(row.montoSaldo) > 0) {
                    if (!puedeRegistrarPagar || row.idEstado == ESTADO_ANULADO) return row.estadoPago || "";
                    return (
                        <Box
                            sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                            onClick={() => abrirRegistroPago(row)}
                        >
                            {"Pagar"}
                        </Box>
                    );
                }
                return row.estadoPago || "";
            }
        },
        // Ultima columna: anular el pago de periodo. No se puede anular un terapiaperiodopago
        // que tiene asignado un comprobante vigente o que ya se encuentra anulado.
        { field: 'eliminar', headerName: '', width: 65, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                const row = cellValues.row;
                if (row.idEstado == ESTADO_ANULADO) return "";
                const tieneComprobanteVigente = row.idComprobante > 0 && Boolean(row.serieNumero);
                const bloqueado = tieneComprobanteVigente;

                const textoTooltip = tieneComprobanteVigente
                    ? "No se puede anular: tiene asignado un comprobante vigente"
                    : "Anular pago de periodo";

                return <Tooltip title={textoTooltip}>
                    <span>
                        <IconButton aria-label="eliminar pago" size='small'
                            style={{ height: 10 }} align={"center"} disabled={!puedeRegistrarPagar || bloqueado}
                            onClick={() => { setPagoSeleccionado(row); setOpenDialogEliminar(true); }}>
                            <IconDelete fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>;
            }
        },
    ];

    const columnasComprobantesSaldo = [
        { field: 'serieNumero', headerName: 'Comprobante', width: 135, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'fecha', headerName: 'Fecha', width: 105, type: 'date'
            , valueFormatter: (value) => moment(value).format('DD/MM/YYYY')
            , sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'pagador', headerName: 'Pagador', width: 230, sortable: false, headerAlign: 'left' },
        { field: 'montoTotal', headerName: 'Total', width: 110, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'saldoDisponible', headerName: 'Saldo Disponible', width: 130, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 110, sortable: false, headerAlign: 'center', align: 'center' },
    ];

    const montosAsignar = calcularMontosFila(pagoAsignar);

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Pagos de Periodos de Terapia
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1} padding={0}>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-filialpago">Filial</InputLabel>
                                    <Select
                                        native
                                        label="Filial"
                                        value={filters.idFilial}
                                        disabled={idFilial > 0}
                                        onChange={(event) => setFilters({ ...filters, idFilial: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idFilial', id: 'input-filialpago' }}
                                        size="small"
                                    >
                                        {idFilial <= 0 && <option key={0} value={0}>{"(Todos)"}</option>}
                                        {listFilial.map((row) => (
                                            <option key={row.id} value={row.id}>{row.descripcion}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-periodopago">Periodo</InputLabel>
                                    <Select
                                        native
                                        label="Periodo"
                                        value={filters.idPeriodo}
                                        onChange={(event) => setFilters({ ...filters, idPeriodo: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idPeriodo', id: 'input-periodopago' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        {listPeriodos.map((row) => (
                                            <option key={row.id} value={row.id}>{row.codigo}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-tipoterapiapago">Tipo Terapia</InputLabel>
                                    <Select
                                        native
                                        label="Tipo Terapia"
                                        value={filters.idTipoTerapia}
                                        onChange={(event) => setFilters({ ...filters, idTipoTerapia: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idTipoTerapia', id: 'input-tipoterapiapago' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        {listTiposTerapias.map((row) => (
                                            <option key={row.id} value={row.id}>{row.descripcion}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-estadopago">Estado</InputLabel>
                                    <Select
                                        native
                                        label="Estado"
                                        value={filters.idEstado}
                                        onChange={(event) => setFilters({ ...filters, idEstado: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idEstado', id: 'input-estadopago' }}
                                        size="small"
                                    >
                                        <option key={2} value={2}>{"Activo"}</option>
                                        <option key={ESTADO_ANULADO} value={ESTADO_ANULADO}>{"Anulado"}</option>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-estadopagopago">Estado del Pago</InputLabel>
                                    <Select
                                        native
                                        label="Estado del Pago"
                                        value={filters.idEstadoPago}
                                        onChange={(event) => setFilters({ ...filters, idEstadoPago: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idEstadoPago', id: 'input-estadopagopago' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        <option key={ESTADO_PAGO_PENDIENTE} value={ESTADO_PAGO_PENDIENTE}>{"Pendiente"}</option>
                                        <option key={ESTADO_PAGO_CANCELADO} value={ESTADO_PAGO_CANCELADO}>{"Cancelado"}</option>
                                        <option key={ESTADO_PAGO_CONSOLIDADO} value={ESTADO_PAGO_CONSOLIDADO}>{"Pago Consolidado"}</option>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                                <Tooltip title={"Buscar pagos"}>
                                    <Button variant="outlined" fullWidth size="medium" onClick={() => { fetchResults() }}>
                                        <IconSearch />
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <DialogYesNo
                open={openDialogDesvincular}
                titulo={"Desvincular Comprobante"}
                texto={
                    pagoDesvincular
                        ? `¿Está seguro de desvincular el comprobante ${pagoDesvincular.serieNumero} del periodo de terapia ${pagoDesvincular.codigoPeriodo || ""} (${pagoDesvincular.participante || ""})? El comprobante liberará el monto asignado.`
                        : "¿Está seguro de desvincular el comprobante de este periodo de terapia?"
                }
                setYes={confirmarDesvincularComprobante}
                setNo={() => { setPagoDesvincular(null); }}
                openClose={() => {
                    if (!desvinculando) {
                        setOpenDialogDesvincular(!openDialogDesvincular);
                        if (openDialogDesvincular) setPagoDesvincular(null);
                    }
                }}
            />

            <DialogYesNo
                open={openDialogEliminar}
                titulo={"Eliminar Pago del Periodo"}
                texto={"Se eliminara el pago y su comprobante quedara sin pago. Si solo quiere dejar de "
                    + "contar, conviene anularlo. Desea continuar?"}
                setYes={eliminarPago}
                setNo={() => { setPagoSeleccionado(null); }}
                openClose={() => {
                    setOpenDialogEliminar(!openDialogEliminar);

                    if (openDialogEliminar) setPagoSeleccionado(null);
                }}
            />

            {/* Ventana con los datos del comprobante. */}
            <ComprobanteDialogo
                idComprobante={comprobanteViendo}
                open={comprobanteViendo > 0}
                onClose={() => setComprobanteViendo(0)}
            />

            {/* Ventana de registro del comprobante del pago del periodo. */}
            <ComprobanteMantenimientoDialogo
                open={openDialogComprobante}
                idComprobante={0}
                prefill={prefillComprobante}
                initialDetails={detalleComprobante}
                detalleSoloLectura={true}
                onClose={() => { setOpenDialogComprobante(false); setPagoComprobante(null); }}
                onSaved={comprobanteGuardado}
            />

            {/* Ventana de registro del pago del comprobante. */}
            <ComprobantePagoDialogo
                open={openDialogPago}
                idComprobante={idComprobantePago}
                onClose={() => setOpenDialogPago(false)}
                onSaved={pagoRegistrado}
            />

            {/* Ventana Dialog: Asignar Comprobante (solo para Administrador y Director) */}
            <Dialog open={openDialogAsignar} onClose={cerrarDialogAsignar} fullWidth maxWidth="sm" PaperComponent={PaperComponentDraggable}>
                <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={10}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {"Asignar Comprobante"}
                            </Typography>
                        </Grid>
                        <Grid item xs={2} display="flex" justifyContent="flex-end">
                            <IconButton aria-label="cerrar" size="small" onClick={cerrarDialogAsignar}>
                                <IconClose fontSize="small" />
                            </IconButton>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} alignItems="center" sx={{ pt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                disabled
                                name="participanteAsignar"
                                label="Participante"
                                value={pagoAsignar?.participante || ""}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                disabled
                                name="terapeutaAsignar"
                                label="Terapeuta"
                                value={pagoAsignar?.terapeuta || ""}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                disabled
                                name="periodoAsignar"
                                label="Periodo"
                                value={pagoAsignar?.codigoPeriodo || ""}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                disabled
                                name="montoTerapiaAsignar"
                                label="Monto Terapia"
                                value={montosAsignar.montoTerapia.toFixed(2)}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ style: { textAlign: 'right' } }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                disabled
                                name="descuentoAsignar"
                                label="Descuento"
                                value={montosAsignar.montoDescuento.toFixed(2)}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ style: { textAlign: 'right' } }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                disabled
                                name="totalAsignar"
                                label="Total"
                                value={montosAsignar.total.toFixed(2)}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ style: { textAlign: 'right' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                disabled
                                name="comprobanteAsignar"
                                label="Comprobante"
                                value={comprobanteElegidoAsignar?.serieNumero || ""}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={"Buscar comprobante con saldo disponible"}>
                                                <span>
                                                    <IconButton
                                                        aria-label="buscar comprobante con saldo disponible"
                                                        size="small"
                                                        edge="end"
                                                        onClick={abrirBusquedaComprobanteSaldo}
                                                    >
                                                        <IconSearch />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                disabled
                                name="saldoDisponibleAsignar"
                                label="Saldo Disponible"
                                value={comprobanteElegidoAsignar
                                    ? (parseFloat(comprobanteElegidoAsignar.saldoDisponible) || 0).toFixed(2)
                                    : ""}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ style: { textAlign: 'right' } }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={cerrarDialogAsignar}>
                        {"Cancelar"}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!comprobanteElegidoAsignar || asignando}
                        onClick={confirmarAsignacionComprobante}
                    >
                        {"Asignar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Ventana de busqueda de comprobantes con Saldo Disponible > 0 */}
            <DialogoBusqueda
                open={buscandoComprobanteSaldo}
                onClose={() => setBuscandoComprobanteSaldo(false)}
                titulo="Buscar Comprobante con Saldo Disponible"
                columns={columnasComprobantesSaldo}
                rows={comprobantesConSaldo}
                loading={buscandoComprobanteSaldoCargando}
                onAceptar={elegirComprobanteParaAsignar}
                filtros={
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={4} sm={2}>
                            <TextField
                                name="serie"
                                label="Serie"
                                value={filtrosComprobanteSaldo.serie}
                                fullWidth
                                size="small"
                                onChange={(e) => setFiltrosComprobanteSaldo({ ...filtrosComprobanteSaldo, serie: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarComprobantesConSaldo(); } }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={2}>
                            <TextField
                                name="numero"
                                label="Numero"
                                value={filtrosComprobanteSaldo.numero}
                                fullWidth
                                size="small"
                                onChange={(e) => setFiltrosComprobanteSaldo({ ...filtrosComprobanteSaldo, numero: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarComprobantesConSaldo(); } }}
                            />
                        </Grid>
                        <Grid item xs={8} sm={5}>
                            <TextField
                                name="pagador"
                                label="Pagador"
                                value={filtrosComprobanteSaldo.pagador}
                                fullWidth
                                size="small"
                                onChange={(e) => setFiltrosComprobanteSaldo({ ...filtrosComprobanteSaldo, pagador: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarComprobantesConSaldo(); } }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={2} display="flex" alignItems="center">
                            <Tooltip title={"Buscar comprobantes"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={buscarComprobantesConSaldo}>
                                    <IconSearch />
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                }
            />

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(false)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
export default Index;
